import React, { useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Trash2 } from 'lucide-react';

/**
 * Props:
 * ─ `title`     – short item name (“role”, “service”…) used in the dialog copy
 * ─ `onConfirm` – called **after** the user types “im sure” and hits Delete
 */
export default function ConfirmDeleteButton({
  title = 'item',
  onConfirm,
}: {
  title?: string;
  onConfirm: () => void | Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const [txt,  setTxt]  = useState('');

  const reset = () => { setTxt(''); setOpen(false); };

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      {/* ── trigger ───────────────────────────────────────── */}
      <Dialog.Trigger asChild>
        <button
          title="Delete"
          className="
            p-1 rounded text-neutral-500 hover:text-rose-500
            transition-colors
          "
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </Dialog.Trigger>

      {/* ── modal ─────────────────────────────────────────── */}
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/60" />
        <Dialog.Content
          className="
            fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2
            w-96 rounded-md bg-neutral-900 border border-neutral-700
            p-6 shadow-xl space-y-4
          "
        >
          <Dialog.Title className="text-lg font-semibold">
            Delete this {title}?
          </Dialog.Title>

          <p className="text-sm text-neutral-400">
            This action is <span className="text-rose-400">irreversible</span>.
            To continue, type <code className="font-mono">im sure</code> below.
          </p>

          <input
            autoFocus
            value={txt}
            onChange={e => setTxt(e.target.value)}
            placeholder="type: im sure"
            className="
              w-full bg-neutral-800 rounded px-3 py-2
              focus:outline-none focus:ring-2 focus:ring-rose-500
            "
          />

          <div className="flex justify-end gap-3 pt-2">
            <Dialog.Close asChild>
              <button
                onClick={reset}
                className="px-3 py-1 rounded bg-neutral-700 hover:bg-neutral-600"
              >
                Cancel
              </button>
            </Dialog.Close>

            <button
              disabled={txt.trim().toLowerCase() !== 'im sure'}
              onClick={async () => {
                await onConfirm();
                reset();
              }}
              className={`
                px-3 py-1 rounded
                ${txt.trim().toLowerCase() === 'im sure'
                  ? 'bg-rose-600 hover:bg-rose-500'
                  : 'bg-neutral-700 cursor-not-allowed'}
              `}
            >
              Delete
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
